import _                from 'lodash';
import Promise          from 'bluebird';
import pluralize        from 'pluralize';
import EntityCollection from './entity_collection';
import RequestBuilder   from './request_builder';
import BackendResponse  from './backend_response';

export default class Relation {
  static logRequests = false;

  constructor(
    backend, route = null, model, associations, includes = [], orders = [],
    pageNumber = 1, pageSize = 1000, logRequests = Relation.logRequests,
  ) {
    this.backend      = backend;
    this.route        = route;
    this.model        = model;
    this.associations = associations;
    this.includes     = includes;
    this.orders       = orders;
    this.pageNumber   = pageNumber;
    this.pageSize     = pageSize;
    this.logRequests  = logRequests;

    if (this.route == null) { this.route = '/' + _.snakeCase(pluralize(this.model.name)); }
  }

  toString() {
    return `${this.constructor.name} { model: ${this.model.name}, includes: ${JSON.stringify(this.includes)} }`;
  }

  all() {
    return RequestBuilder.for(this).index().then(this.deserializeMany.bind(this));
  }

  where(conditions) {
    return RequestBuilder.for(this).filteredIndex(conditions).then(this.deserializeMany.bind(this));
  }

  none() {
    return Promise.resolve(this.emptyCollection());
  }

  emptyCollection() {
    return EntityCollection.emptyFor(this.model, this.associations);
  }

  collectionWith(entities) {
    return EntityCollection.for(entities, this.model, this.associations);
  }

  find(pk) {
    return RequestBuilder.for(this).get(pk).then(this.deserializeOne.bind(this));
  }

  create(entity) {
    return RequestBuilder.for(this).post(entity).then(this.deserializeOne.bind(this));
  }

  update(pkOrEntity, attributes) {
    const [pk, newAttributes] = this._pkAndAttributesFor(pkOrEntity, attributes);

    return RequestBuilder.for(this).put(pk, newAttributes).then(this.deserializeOne.bind(this));
  }

  partialUpdate(pk, attributes) {
    return RequestBuilder.for(this).patch(pk, attributes).then(this.deserializeOne.bind(this));
  }

  delete(pk) {
    return RequestBuilder.for(this).delete(pk).then(() => pk);
  }

  findMany(pkeys) {
    return Promise.mapSeries(pkeys, this.find.bind(this));
  }

  createMany(entities) {
    return RequestBuilder.for(this).postMany(entities).then(this.deserializeMany.bind(this));
  }

  deleteMany(pkeysOrCollection) {
    const pkeys = pkeysOrCollection.constructor === EntityCollection ?
      pkeysOrCollection.pkeys() : pkeysOrCollection;

    return Promise.mapSeries(pkeys, this.delete.bind(this));
  }

  findBy(conditions) {
    return this.page(1, 1).where(conditions).then(_.method('first'));
  }

  // very inefficient, only for testing purposes
  deleteAll() {
    return this.all().then(collection => this.deleteMany(collection));
  }

  with(...includes) {
    includes = _.uniq(_.concat(this.includes, includes))

    return new this.constructor(
      this.backend, this.route, this.model, this.associations, includes,
      this.orders, this.pageNumber, this.pageSize, this.logRequests
    );
  }

  orderBy(...orders) {
    orders = _.uniq(_.concat(this.orders, orders))

    return new this.constructor(
      this.backend, this.route, this.model, this.associations, this.includes,
      orders, this.pageNumber, this.pageSize, this.logRequests
    );
  }

  page(pageNumber, pageSize = 20) {
    return new this.constructor(
      this.backend, this.route, this.model, this.associations, this.includes,
      this.orders, pageNumber, pageSize, this.logRequests
    );
  }

  withLogging() {
    return new this.constructor(
      this.backend, this.route, this.model, this.associations, this.includes,
      this.orders, this.pageNumber, this.pageSize, true
    );
  }

  deserializeOne(response) {
    return new BackendResponse(
      response, this.model, this.associations, this._includedModels()
    ).entity();
  }

  deserializeMany(response) {
    return new BackendResponse(
      response, this.model, this.associations, this._includedModels(), this.pageNumber, this.pageSize
    ).collection();
  }

  _includedModels() {
    return this.associations.destinationModelsForPaths(this.model, this.includes);
  }

  _pkAndAttributesFor(pkOrEntity, attributes) {
    let pk;

    if (attributes) {
      pk = pkOrEntity;
    } else {
      ({ pk }    = pkOrEntity);
      attributes = _.omit(pkOrEntity, ['pk']);
    }

    return [pk, attributes];
  }
};
